import React, { useEffect } from 'react'
import { Redirect, Route, useLocation } from 'react-router-dom'
import CookiesStorageService from './CookiesStorageService'
import { isNotEmpty } from '../utils/helpers'
import AppRoutes from './AppRoutes'
import getRequiredAuthRedirect from './AuthRedirectService'
import ResponsiveAppBar from '../components/ResponsiveAppBar'

const ADMIN_TYPES = ['sys_admin', 'customer_admin']

const AuthRoute = ({
  component: Component,
  sysAdminComponent: SysAdminComponent,
  csAdminComponent: CsAdminComponent,
  path,
  withAppBar = false,
  appBarProps,
  ...props
}) => {
  const location = useLocation()
  const baseUri = process.env.REACT_APP_ROOT_ROUTE || ''
  const isAuthenticated = isNotEmpty(CookiesStorageService.getAccessToken())
  const adminType = CookiesStorageService.getAdminType()
  // Admin routes are configured by passing one or both role-specific components.
  const isAdminRoute = Boolean(SysAdminComponent || CsAdminComponent)

  // Reset scroll when navigating between pages that share this route wrapper.
  useEffect(() => () => {
    window.scrollTo({ top: 0 })
  }, [location.pathname])

  // Protected routes require a session before checking first-login or consent redirects.
  if (!isAuthenticated) {
    return <Redirect to={baseUri + AppRoutes.notAuthorized} />
  }

  const requiredRedirect = getRequiredAuthRedirect()

  // Enforce required account setup steps before allowing access to protected pages.
  const isCurrentRequiredRedirectRoute = requiredRedirect === path

  if (requiredRedirect && !isCurrentRequiredRedirectRoute) {
    return <Redirect to={baseUri + requiredRedirect} />
  }

  // Only supported admin roles can enter admin routes; other signed-in users go home.
  if (isAdminRoute && !ADMIN_TYPES.includes(adminType)) {
    return <Redirect to={baseUri + AppRoutes.landing} />
  }

  const resolvedAppBarProps = appBarProps || { adminType }

  return (
    <Route
      path={path}
      render={(routeProps) => (
        <>
          {(withAppBar || isAdminRoute) && <ResponsiveAppBar {...resolvedAppBarProps} />}
          {isAdminRoute ? (
            <>
              {/* Render the matching admin experience for the role stored in cookies. */}
              {adminType === 'sys_admin' && SysAdminComponent && <SysAdminComponent />}
              {adminType === 'customer_admin' && CsAdminComponent && <CsAdminComponent />}
            </>
          ) : (
            <Component {...routeProps} />
          )}
        </>
      )}
      {...props}
    />
  )
}

export default AuthRoute
